import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

/* ---------- Activities ---------- */
export function useActivities(dealId, filters = {}) {
  return useQuery({
    queryKey: ["activities", dealId, filters],
    queryFn: async () => {
      if (!dealId) return [];
      const { data } = await api.get(`/deals/${dealId}/activities`, { params: filters });
      return data;
    },
    enabled: !!dealId,
  });
}

export function useCreateActivity(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await api.post(`/deals/${dealId}/activities`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities", dealId] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal-next-step", dealId] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });
}

export function useUpdateActivity(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/activities/${id}`, payload)).data,
    onMutate: async ({ id, payload }) => {
      await qc.cancelQueries({ queryKey: ["activities", dealId] });
      const prev = qc.getQueriesData({ queryKey: ["activities", dealId] });
      qc.setQueriesData({ queryKey: ["activities", dealId] }, (old) =>
        Array.isArray(old) ? old.map((a) => (a.id === id ? { ...a, ...payload } : a)) : old
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev?.forEach(([k, v]) => qc.setQueryData(k, v)),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["activities", dealId] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal-next-step", dealId] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });
}

export function useCompleteActivity(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/activities/${id}/complete`)).data,
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["activities", dealId] });
      const prev = qc.getQueriesData({ queryKey: ["activities", dealId] });
      qc.setQueriesData({ queryKey: ["activities", dealId] }, (old) =>
        Array.isArray(old)
          ? old.map((a) => (a.id === id ? { ...a, status: "completed", completed_date: new Date().toISOString() } : a))
          : old
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev?.forEach(([k, v]) => qc.setQueryData(k, v)),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["activities", dealId] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal-next-step", dealId] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });
}

export function useDeleteActivity(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/activities/${id}`)).data,
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["activities", dealId] });
      const prev = qc.getQueriesData({ queryKey: ["activities", dealId] });
      qc.setQueriesData({ queryKey: ["activities", dealId] }, (old) =>
        Array.isArray(old) ? old.filter((a) => a.id !== id) : old
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev?.forEach(([k, v]) => qc.setQueryData(k, v)),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["activities", dealId] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["deal-next-step", dealId] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });
}

/* ---------- Notes ---------- */
export function useNotes(dealId) {
  return useQuery({
    queryKey: ["notes", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}/notes`)).data,
    enabled: !!dealId,
  });
}
export function useCreateNote(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await api.post(`/deals/${dealId}/notes`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", dealId] }),
  });
}
export function useUpdateNote(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/notes/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", dealId] }),
  });
}
export function useDeleteNote(dealId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/notes/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes", dealId] }),
  });
}

/* ---------- Deals ---------- */
export function useDeals() {
  return useQuery({
    queryKey: ["deals"],
    queryFn: async () => (await api.get("/deals")).data,
  });
}
export function useDeal(id) {
  return useQuery({
    queryKey: ["deal", id],
    queryFn: async () => (await api.get(`/deals/${id}`)).data,
    enabled: !!id,
  });
}
export function useDealNextStep(id) {
  return useQuery({
    queryKey: ["deal-next-step", id],
    queryFn: async () => (await api.get(`/deals/${id}/next_step`)).data,
    enabled: !!id,
  });
}
export function useMoveDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, stage, before_id, after_id }) =>
      (await api.patch(`/deals/${id}/move`, { stage, before_id: before_id || null, after_id: after_id || null })).data,
    onMutate: async ({ id, stage }) => {
      await qc.cancelQueries({ queryKey: ["deals"] });
      const prev = qc.getQueryData(["deals"]);
      qc.setQueryData(["deals"], (old) =>
        Array.isArray(old) ? old.map((d) => (d.id === id ? { ...d, stage } : d)) : old
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(["deals"], ctx.prev),
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      if (vars?.id) qc.invalidateQueries({ queryKey: ["deal-next-step", vars.id] });
    },
  });
}

/* ---------- Settings: stages & statuses ---------- */
export function useStages() {
  return useQuery({
    queryKey: ["stages"],
    queryFn: async () => (await api.get("/settings/stages")).data,
  });
}
export function useActivityStatuses() {
  return useQuery({
    queryKey: ["activity-statuses"],
    queryFn: async () => (await api.get("/settings/statuses")).data,
  });
}
export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/deals/${id}`, payload)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      if (data?.id) {
        qc.invalidateQueries({ queryKey: ["deal", data.id] });
        qc.invalidateQueries({ queryKey: ["deal-next-step", data.id] });
      }
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });
}
export function useUpdateDealStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, stage }) => (await api.patch(`/deals/${id}/stage`, { stage })).data,
    onMutate: async ({ id, stage }) => {
      await qc.cancelQueries({ queryKey: ["deals"] });
      const prev = qc.getQueryData(["deals"]);
      qc.setQueryData(["deals"], (old) =>
        Array.isArray(old) ? old.map((d) => (d.id === id ? { ...d, stage } : d)) : old
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(["deals"], ctx.prev),
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      if (vars?.id) qc.invalidateQueries({ queryKey: ["deal-next-step", vars.id] });
    },
  });
}

/* ---------- Tasks (related to deals) ---------- */
export function useDealTasks(dealId) {
  return useQuery({
    queryKey: ["deal-tasks", dealId],
    queryFn: async () => (await api.get("/tasks", { params: { related_deal_id: dealId } })).data,
    enabled: !!dealId,
  });
}
export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await api.post("/tasks", payload)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      if (data?.related_deal_id) qc.invalidateQueries({ queryKey: ["deal-tasks", data.related_deal_id] });
    },
  });
}
export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/tasks/${id}`, payload)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      if (data?.related_deal_id) qc.invalidateQueries({ queryKey: ["deal-tasks", data.related_deal_id] });
    },
  });
}
export function useToggleTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/tasks/${id}/toggle`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      if (data?.related_deal_id) qc.invalidateQueries({ queryKey: ["deal-tasks", data.related_deal_id] });
    },
  });
}
export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => (await api.delete(`/tasks/${id}`)).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      if (vars?.related_deal_id) qc.invalidateQueries({ queryKey: ["deal-tasks", vars.related_deal_id] });
    },
  });
}
